"use client";

import Link from "next/link";

import { useI18n } from "@/components/i18n-provider";
import type { TalentPoolUploadChoice } from "@/lib/indexed-db";
import { cn } from "@/lib/utils";

interface CvTalentPoolChoiceFieldProps {
  value: TalentPoolUploadChoice | null;
  onChange: (value: TalentPoolUploadChoice) => void;
  /** Shown once the person tried to submit without answering. */
  showError: boolean;
  /** Keeps ids unique when the desktop and mobile forms share a page. */
  idPrefix: string;
  /** Matches the label and control scale of the surrounding form. */
  size: "desktop" | "mobile";
}

/**
 * The required pre-upload talent-pool question. No option is preselected:
 * consent has to be an active choice, and both answers get the free review.
 *
 * It mirrors the review purpose control above it, so the question reads as a
 * normal form field rather than a legal footnote. Native radios stay in the
 * DOM (visually hidden) for arrow-key navigation and screen reader semantics.
 */
export function CvTalentPoolChoiceField({
  value,
  onChange,
  showError,
  idPrefix,
  size,
}: CvTalentPoolChoiceFieldProps) {
  const { dictionary, locale } = useI18n();
  const copy = dictionary.cvUploadForm.fields.discoverable;
  const legendId = `${idPrefix}-talent-pool-legend`;
  const helperId = `${idPrefix}-talent-pool-helper`;
  const errorId = `${idPrefix}-talent-pool-error`;
  const options: Array<{ value: TalentPoolUploadChoice; label: string }> = [
    { value: "yes", label: copy.yes },
    { value: "no", label: copy.no },
  ];

  return (
    <fieldset
      aria-describedby={cn(helperId, showError && errorId)}
      aria-invalid={showError || undefined}
      aria-required="true"
      className="space-y-2"
    >
      <legend
        id={legendId}
        className={cn(
          "font-medium text-text-heading",
          size === "desktop" ? "text-base leading-6" : "text-sm leading-5",
        )}
      >
        {copy.label}
      </legend>

      <div className="flex gap-2">
        {options.map((option) => {
          const id = `${idPrefix}-talent-pool-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={id}
              className={cn(
                "flex min-w-0 flex-1 cursor-pointer items-center justify-center rounded-lg border bg-background px-3 text-center text-sm font-medium leading-5 text-text-heading shadow-button transition-colors",
                size === "desktop" ? "h-10" : "min-h-11 py-2",
                "hover:border-text-muted",
                "has-[:checked]:border-brand-primary has-[:checked]:bg-status-error-bg",
                "has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand-primary",
                showError ? "border-brand-primary/60" : "border-border-input",
              )}
            >
              <input
                id={id}
                type="radio"
                name={`${idPrefix}-talent-pool`}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              {option.label}
            </label>
          );
        })}
      </div>

      {showError ? (
        <p id={errorId} className="text-xs text-brand-primary">
          {copy.required}
        </p>
      ) : null}

      <p id={helperId} className="text-xs leading-5 text-text-body">
        {copy.helper}{" "}
        <Link
          href={`/${locale}/privacy`}
          target="_blank"
          className="font-medium text-text-heading underline underline-offset-2 hover:text-brand-primary"
        >
          {copy.privacyLink}
        </Link>
      </p>
    </fieldset>
  );
}
